/**
 * PeriodCheckPanel — Ausgebaute Prüfansicht für Periodenabschlüsse (Juni 2026).
 *
 * Der SB wählt eine Periode (Monat/Quartal/Jahr) und startet die Beleg-Prüfung:
 * fehlende Belege, unkategorisierte Ausgaben, USt-Plausibilität, Duplikate,
 * Rechnungsstatus, offene Vorgänge u.a. — gleiche Checks wie der
 * Abschluss-Pre-Check, aber ad-hoc OHNE angelegten Abschluss.
 *
 * Klick auf einen Befund mit Beleg-Bezug öffnet den Detail-Drawer
 * (Deeplink-Event `ivoliq:focus-item`, wie im PeriodClosureWizard).
 */
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { onAdvisorChange } from "@/utils/advisorBus";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ClipboardCheck, CheckCircle2, AlertTriangle, XCircle, Info, Loader2,
  Receipt, Tags, Percent, FileText, Copy, Repeat, Gauge, MessageSquare, Search,
  Ban, Trash2, Undo2, Send, X, ExternalLink,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const H = () => ({ Authorization: `Bearer ${localStorage.getItem("advisor_token")}` });

const MONTH_NAMES = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];

// Befund-Codes → thematische Gruppen (Reihenfolge = Anzeige-Reihenfolge)
const GROUPS = [
  { id: "receipts", codes: ["missing_receipt"], title: "Fehlende Belege", icon: Receipt },
  { id: "categories", codes: ["uncategorized_expense"], title: "Unkategorisierte Ausgaben", icon: Tags },
  { id: "vat", codes: ["vat_mismatch", "unexpected_vat_rate", "missing_vat_amount", "missing_vat_rate"], title: "USt-Plausibilität", icon: Percent },
  { id: "invoices", codes: ["stale_sent_invoice", "missing_paid_at"], title: "Rechnungsstatus", icon: FileText },
  { id: "duplicates", codes: ["possible_duplicate"], title: "Mögliche Duplikate", icon: Copy },
  { id: "recurring", codes: ["missing_recurring"], title: "Vermutete fehlende wiederkehrende Rechnungen", icon: Repeat },
  { id: "ku", codes: ["kleinunternehmer_exceeded", "kleinunternehmer_approaching", "kleinunternehmer_yearly_exceeded"], title: "Kleinunternehmer-Grenze", icon: Gauge },
  { id: "review", codes: ["items_unreviewed"], title: "Prüfstatus", icon: ClipboardCheck },
  { id: "threads", codes: ["open_inbox_thread"], title: "Offene Vorgänge", icon: MessageSquare },
];

const SEV_ICON = {
  error: <XCircle className="h-3.5 w-3.5 text-rose-600 shrink-0 mt-0.5" />,
  warn: <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />,
  info: <Info className="h-3.5 w-3.5 text-sky-500 shrink-0 mt-0.5" />,
};

function groupFindings(findings) {
  const used = new Set();
  const out = [];
  for (const g of GROUPS) {
    const items = findings.filter((f) => g.codes.includes(f.code));
    items.forEach((f) => used.add(f));
    if (items.length > 0) out.push({ ...g, items });
  }
  const rest = findings.filter((f) => !used.has(f));
  if (rest.length > 0) out.push({ id: "other", title: "Sonstiges", icon: Info, items: rest });
  return out;
}

export default function PeriodCheckPanel({ clientId, year, canEdit = false }) {
  const [periodType, setPeriodType] = useState("quarter");
  const [periodValue, setPeriodValue] = useState(1);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showDismissed, setShowDismissed] = useState(false);
  const [modal, setModal] = useState(null); // { mode: 'dismiss'|'query', finding }
  const [modalText, setModalText] = useState("");

  const runCheck = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/advisor/clients/${clientId}/period-precheck`, {
        year,
        period_type: periodType,
        period_value: periodType === "year" ? null : periodValue,
      }, { headers: H() });
      setResult(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Prüfung fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  // Cross-Tab-Auto-Refresh (Juni 2026): Befunde werden in neuem Tab geöffnet.
  // Speichert der SB dort eine Änderung, läuft die Prüfung hier automatisch neu.
  const runCheckRef = useRef(runCheck);
  runCheckRef.current = runCheck;
  const hasResultRef = useRef(false);
  hasResultRef.current = !!result;
  useEffect(() => {
    return onAdvisorChange(clientId, () => {
      if (hasResultRef.current) {
        runCheckRef.current();
        toast.message("Prüfung nach Änderung aktualisiert");
      }
    });
  }, [clientId]);

  const openItem = (f) => {
    if (!f.entity_id || !["expense", "invoice"].includes(f.entity_type)) return;
    // Immer in NEUEM Tab öffnen, damit die Prüfung erhalten bleibt.
    const yr = result?.period?.year || year;
    window.open(`/advisor/client/${clientId}?focus=${f.entity_type}:${f.entity_id}&year=${yr}`, "_blank", "noopener");
  };

  // Stabiler Identifikator eines Befunds für Ablehnen/Wiederherstellen.
  const keyPayload = (f) => (
    f.code === "missing_recurring"
      ? { code: f.code, vendor_key: f.hint?.vendor_key, missing_period: f.hint?.missing_period }
      : { code: f.code, entity_type: f.entity_type, entity_id: f.entity_id }
  );

  const openDismiss = (f) => { setModal({ mode: "dismiss", finding: f }); setModalText(""); };
  const openQuery = (f) => {
    setModal({ mode: "query", finding: f });
    setModalText(`Rückfrage zur Prüfung: ${f.message}\n\nBitte um Klärung / fehlenden Beleg.`);
  };
  const closeModal = () => { setModal(null); setModalText(""); };

  const submitDismiss = async () => {
    if (!modalText.trim()) { toast.error("Bitte eine Begründung angeben."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/advisor/clients/${clientId}/findings/dismiss`,
        { ...keyPayload(modal.finding), reason: modalText.trim() }, { headers: H() });
      toast.success("Befund abgelehnt");
      closeModal();
      setShowDismissed(true);
      runCheck();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ablehnen fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const submitQuery = async () => {
    if (!modalText.trim()) { toast.error("Bitte eine Nachricht eingeben."); return; }
    setBusy(true);
    try {
      const f = modal.finding;
      const linkable = ["expense", "invoice"].includes(f.entity_type) && f.entity_id;
      await axios.post(`${API}/advisor/clients/${clientId}/comments`, {
        entity_type: linkable ? f.entity_type : (f.entity_type || "note"),
        entity_id: linkable ? f.entity_id : null,
        text: modalText.trim(),
      }, { headers: H() });
      toast.success("Rückfrage an den Mandanten gesendet");
      closeModal();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Senden fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const openBulkQuery = () => {
    const items = result?.findings || [];
    const lines = items.map((f) => `• ${f.message}`).join("\n");
    setModal({ mode: "bulk" });
    setModalText(
      `Rückfragen zur Belegprüfung ${periodLabel || ""}:\n\n` +
      `Bitte um Klärung bzw. Nachreichung zu folgenden Punkten:\n${lines}\n\nVielen Dank!`
    );
  };

  const submitBulkQuery = async () => {
    if (!modalText.trim()) { toast.error("Bitte eine Nachricht eingeben."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/advisor/clients/${clientId}/findings/bulk-query`,
        { text: modalText.trim(), period_label: periodLabel }, { headers: H() });
      toast.success("Sammel-Rückfrage im Posteingang gesendet");
      closeModal();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Senden fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const restore = async (f) => {
    setBusy(true);
    try {
      await axios.post(`${API}/advisor/clients/${clientId}/findings/restore`, keyPayload(f), { headers: H() });
      toast.success("Befund wiederhergestellt");
      runCheck();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const markDuplicate = async (f) => {
    if (!f.entity_id) return;
    if (!window.confirm("Diese Ausgabe als Duplikat bestätigen und die Doppelbuchung entfernen?\n\nDer Eintrag wird gelöscht (mit Audit-Eintrag). Dies kann nicht rückgängig gemacht werden.")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/advisor/clients/${clientId}/expense/${f.entity_id}/mark-duplicate`,
        { reason: "Als Duplikat bestätigt (Belegprüfung)" }, { headers: H() });
      toast.success("Duplikat entfernt");
      runCheck();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
    } finally { setBusy(false); }
  };

  // Juni 2026 — Schnellaktion: fehlenden Satz als bewusst 0 % (steuerfrei) bestätigen.
  const confirmZeroVat = async (f) => {
    if (!f.entity_id) return;
    if (!window.confirm("Diese Ausgabe als 0 % (steuerfrei) bestätigen?\n\nDer MwSt-Satz wird auf 0 % gesetzt (mit Audit-Eintrag).")) return;
    setBusy(true);
    try {
      await axios.put(`${API}/advisor/clients/${clientId}/expense/${f.entity_id}`,
        { vat_rate: 0 }, { headers: H() });
      toast.success("Als 0 % (steuerfrei) bestätigt");
      runCheck();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const periodLabel = result
    ? (result.period.period_type === "year"
        ? `${result.period.year}`
        : result.period.period_type === "quarter"
          ? `Q${result.period.period_value} ${result.period.year}`
          : `${MONTH_NAMES[(result.period.period_value || 1) - 1]} ${result.period.year}`)
    : null;

  const groups = result ? groupFindings(result.findings) : [];
  const s = result?.summary;
  const dismissed = result?.dismissed || [];
  const clean = result && result.findings.length === 0;

  return (
    <div className="bg-white border border-zinc-200 rounded-xl" data-testid="period-check-panel">
      <div className="px-4 py-3 border-b border-zinc-100 flex items-center gap-2">
        <Search className="w-4 h-4 text-slate-500" />
        <div className="flex flex-col">
          <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Belegprüfung pro Periode</span>
          <span className="text-[11px] text-slate-400 normal-case">Vorprüfung vor dem Periodenabschluss · Befunde öffnen in einem neuen Tab</span>
        </div>
      </div>

      {/* Perioden-Auswahl + Start */}
      <div className="px-4 py-3 flex flex-wrap items-end gap-3 border-b border-zinc-100">
        <div>
          <Label className="text-xs text-slate-600 mb-1 block">Zeitraum</Label>
          <Select value={periodType} onValueChange={(v) => { setPeriodType(v); setPeriodValue(1); }}>
            <SelectTrigger className="h-8 text-sm rounded-md w-32" data-testid="check-period-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="month">Monat</SelectItem>
              <SelectItem value="quarter">Quartal</SelectItem>
              <SelectItem value="year">Ganzes Jahr</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {periodType !== "year" && (
          <div>
            <Label className="text-xs text-slate-600 mb-1 block">{periodType === "quarter" ? "Quartal" : "Monat"}</Label>
            <Select value={String(periodValue)} onValueChange={(v) => setPeriodValue(parseInt(v))}>
              <SelectTrigger className="h-8 text-sm rounded-md w-28" data-testid="check-period-value"><SelectValue /></SelectTrigger>
              <SelectContent>
                {periodType === "quarter"
                  ? [1, 2, 3, 4].map((q) => <SelectItem key={q} value={String(q)}>Q{q}</SelectItem>)
                  : MONTH_NAMES.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <Button size="sm" onClick={runCheck} disabled={loading}
          className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 rounded-md text-xs"
          data-testid="run-period-check-btn">
          {loading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <ClipboardCheck className="w-3.5 h-3.5 mr-1.5" />}
          Prüfung starten ({year})
        </Button>
        {s && (
          <div className="flex items-center gap-1.5 ml-auto" data-testid="check-summary">
            {s.errors > 0 && <span className="text-[11px] px-2 py-0.5 rounded-full bg-rose-50 border border-rose-200 text-rose-700 font-medium">{s.errors} Fehler</span>}
            {s.warnings > 0 && <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 font-medium">{s.warnings} Warnungen</span>}
            {s.infos > 0 && <span className="text-[11px] px-2 py-0.5 rounded-full bg-sky-50 border border-sky-200 text-sky-700 font-medium">{s.infos} Hinweise</span>}
          </div>
        )}
      </div>

      {/* Sammel-Rückfrage (In-App-Posteingang) */}
      {result && result.findings.length > 0 && (
        <div className="px-4 py-2 border-b border-zinc-100 flex items-center justify-between bg-blue-50/40" data-testid="bulk-query-bar">
          <span className="text-[11px] text-slate-600">
            {result.findings.length} offene Befunde in {periodLabel}
          </span>
          <button onClick={openBulkQuery} disabled={busy}
            className="inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-md bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
            data-testid="bulk-query-btn">
            <Send className="w-3 h-3" />Sammel-Rückfrage an Mandant
          </button>
        </div>
      )}

      {/* Ergebnis */}
      {!result && !loading && (
        <p className="px-4 py-6 text-sm text-slate-400 text-center">
          Wähle eine Periode und starte die Prüfung — geprüft werden Belege, Kategorien,
          USt-Plausibilität, Duplikate, Rechnungsstatus und offene Vorgänge.
        </p>
      )}
      {clean && (
        <div className="px-4 py-6 flex items-center justify-center gap-2 text-emerald-700" data-testid="check-all-clean">
          <CheckCircle2 className="w-5 h-5" />
          <span className="text-sm font-medium">Alles sauber — keine offenen Auffälligkeiten in {periodLabel}.</span>
        </div>
      )}
      {result && !clean && (
        <div className="divide-y divide-zinc-100" data-testid="check-results">
          {groups.map((g) => {
            const GIcon = g.icon;
            const worst = g.items.some((i) => i.severity === "error") ? "error"
              : g.items.some((i) => i.severity === "warn") ? "warn" : "info";
            return (
              <div key={g.id} className="px-4 py-3" data-testid={`check-group-${g.id}`}>
                <div className="flex items-center gap-2 mb-2">
                  {SEV_ICON[worst]}
                  <GIcon className="w-3.5 h-3.5 text-slate-500" />
                  <span className="text-sm font-medium text-slate-800">{g.title}</span>
                  <span className="text-[11px] text-slate-400">({g.items.length})</span>
                </div>
                <ul className="space-y-1.5 pl-6">
                  {g.items.map((f, i) => {
                    const linkable = f.entity_id && ["expense", "invoice"].includes(f.entity_type);
                    const isDup = f.code === "possible_duplicate";
                    return (
                      <li key={`${f.code}-${i}`}
                        className="flex items-start justify-between gap-3 text-xs text-slate-600 leading-snug group"
                        data-testid={`check-finding-${f.code}-${i}`}>
                        <span
                          onClick={() => openItem(f)}
                          className={`flex items-start gap-2 min-w-0 ${linkable ? "cursor-pointer hover:text-emerald-700 hover:underline" : ""}`}>
                          {SEV_ICON[f.severity] || SEV_ICON.info}
                          <span>{f.message}</span>
                        </span>
                        <span className="flex items-center gap-1 shrink-0">
                          {linkable && (
                            <button onClick={() => openItem(f)} title="In neuem Tab öffnen"
                              className="p-1 rounded text-slate-400 hover:text-emerald-700 hover:bg-emerald-50"
                              data-testid={`finding-open-${f.code}-${i}`}>
                              <ExternalLink className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {isDup && canEdit && (
                            <button onClick={() => markDuplicate(f)} disabled={busy} title="Als Duplikat bestätigen & entfernen"
                              className="p-1 rounded text-slate-400 hover:text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                              data-testid={`finding-mark-duplicate-${i}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {f.code === "missing_vat_rate" && canEdit && f.entity_id && (
                            <button onClick={() => confirmZeroVat(f)} disabled={busy} title="Als 0 % (steuerfrei) bestätigen"
                              className="p-1 rounded text-slate-400 hover:text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                              data-testid={`finding-confirm-zero-vat-${i}`}>
                              <CheckCircle2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                          <button onClick={() => openQuery(f)} disabled={busy} title="Rückfrage an den Mandanten"
                            className="p-1 rounded text-slate-400 hover:text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                            data-testid={`finding-query-${f.code}-${i}`}>
                            <Send className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => openDismiss(f)} disabled={busy} title="Befund ablehnen (mit Begründung)"
                            className="p-1 rounded text-slate-400 hover:text-amber-700 hover:bg-amber-50 disabled:opacity-50"
                            data-testid={`finding-dismiss-${f.code}-${i}`}>
                            <Ban className="w-3.5 h-3.5" />
                          </button>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      {/* Abgelehnte Befunde (GoBD-dokumentiert) */}
      {result && dismissed.length > 0 && (
        <div className="px-4 py-3 border-t border-zinc-100 bg-zinc-50/50" data-testid="check-dismissed-section">
          <button onClick={() => setShowDismissed((v) => !v)}
            className="text-[11px] text-slate-500 hover:text-slate-800 flex items-center gap-1"
            data-testid="toggle-dismissed-findings">
            <Ban className="w-3 h-3" />
            {showDismissed ? "Abgelehnte Befunde ausblenden" : `${dismissed.length} abgelehnte(r) Befund(e) anzeigen`}
          </button>
          {showDismissed && (
            <ul className="mt-2 space-y-1.5">
              {dismissed.map((f, i) => (
                <li key={`dism-${f.code}-${i}`} className="flex items-start justify-between gap-3 text-[11px] text-slate-500"
                  data-testid={`dismissed-finding-${f.code}-${i}`}>
                  <span className="min-w-0">
                    <span className="line-through decoration-slate-300">{f.message}</span>
                    {f.dismiss_reason && <span className="not-italic text-slate-400"> — Begründung: „{f.dismiss_reason}"</span>}
                    {f.dismissed_by && <span className="text-slate-400"> ({f.dismissed_by})</span>}
                  </span>
                  <button onClick={() => restore(f)} disabled={busy} title="Wiederherstellen"
                    className="shrink-0 inline-flex items-center gap-1 text-slate-500 hover:text-slate-900 disabled:opacity-50"
                    data-testid={`restore-finding-${f.code}-${i}`}>
                    <Undo2 className="w-3 h-3" />Wiederherstellen
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Modal: Ablehnen mit Begründung / Rückfrage an Mandant / Sammel-Rückfrage */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="finding-action-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-sm font-semibold text-slate-900">
                {modal.mode === "dismiss" ? "Befund ablehnen"
                  : modal.mode === "bulk" ? "Sammel-Rückfrage an den Mandanten"
                  : "Rückfrage an den Mandanten"}
              </h3>
              <button onClick={closeModal} className="text-slate-400 hover:text-slate-700" data-testid="finding-modal-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-slate-500 mb-3">
              {modal.mode === "bulk"
                ? "Wird als eine Nachricht im Posteingang des Mandanten erstellt (keine E-Mail)."
                : modal.finding?.message}
            </p>
            <textarea
              value={modalText}
              onChange={(e) => setModalText(e.target.value)}
              rows={modal.mode === "dismiss" ? 3 : 7}
              autoFocus
              placeholder={modal.mode === "dismiss" ? "Begründung (z. B. kein Duplikat, Beleg liegt vor, einmalig) …" : "Nachricht an den Mandanten …"}
              className="w-full text-sm border border-slate-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-emerald-400"
              data-testid="finding-modal-textarea"
            />
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={closeModal} disabled={busy}
                className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
                data-testid="finding-modal-cancel">
                Abbrechen
              </button>
              <button
                onClick={modal.mode === "dismiss" ? submitDismiss : modal.mode === "bulk" ? submitBulkQuery : submitQuery}
                disabled={busy || !modalText.trim()}
                className={`px-3 py-1.5 text-sm rounded-lg text-white disabled:opacity-50 ${modal.mode === "dismiss" ? "bg-amber-600 hover:bg-amber-700" : "bg-blue-600 hover:bg-blue-700"}`}
                data-testid="finding-modal-confirm">
                {busy ? "…" : (modal.mode === "dismiss" ? "Ablehnen" : "Senden")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
